import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, DatePicker, Spin, message, Table } from 'antd';
import { UserAddOutlined, UserDeleteOutlined, RiseOutlined, DollarOutlined, TrophyOutlined } from '@ant-design/icons';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { dashboardService } from '../services/api';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

const DashboardComercial = () => {
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [mesAno, setMesAno] = useState(dayjs());

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#ffc658'];

  useEffect(() => {
    carregarDados();
  }, [mesAno]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const params = {
        mes: mesAno.month() + 1,
        ano: mesAno.year()
      };

      const response = await dashboardService.getComercial(params);
      setDados(response.data);
    } catch (error) {
      message.error('Erro ao carregar dados do dashboard comercial');
      console.error(error);
    }
    setLoading(false);
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  if (loading || !dados) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin size="large" tip="Carregando dashboard..." />
      </div>
    );
  }

  const { kpis, matriculas_por_modalidade, receita_por_escola, evolucao_matriculas } = dados;

  const colunasReceitaEscola = [
    {
      title: 'Escola',
      dataIndex: 'escola',
      key: 'escola',
      width: 250,
    },
    {
      title: 'Alunos',
      dataIndex: 'alunos',
      key: 'alunos',
      align: 'center',
      width: 100,
    },
    {
      title: 'Receita',
      dataIndex: 'receita',
      key: 'receita',
      align: 'right',
      width: 150,
      render: (valor) => formatarMoeda(valor),
    },
    {
      title: 'Ticket Médio',
      dataIndex: 'ticket_medio',
      key: 'ticket_medio',
      align: 'right',
      width: 150,
      render: (valor) => formatarMoeda(valor),
    },
  ];

  return (
    <div className="dashboard-page" style={{ padding: '24px' }}>
      {/* Cabeçalho */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <h1 style={{ margin: 0 }}>Dashboard Comercial</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Captação e retenção de alunos</p>
        </Col>
        <Col>
          <DatePicker
            picker="month"
            value={mesAno}
            onChange={(date) => setMesAno(date || dayjs())}
            format="MMMM/YYYY"
            style={{ width: 200 }}
          />
        </Col>
      </Row>

      {/* KPIs Principais */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Novas Matrículas"
              value={kpis.novas_matriculas.total}
              prefix={<UserAddOutlined />}
              suffix={
                <span style={{ fontSize: '14px', color: kpis.novas_matriculas.crescimento >= 0 ? '#3f8600' : '#cf1322' }}>
                  {kpis.novas_matriculas.crescimento >= 0 ? '↑' : '↓'} {Math.abs(kpis.novas_matriculas.crescimento).toFixed(1)}%
                </span>
              }
              valueStyle={{ color: '#52c41a' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              vs mês anterior
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Alunos que Saíram"
              value={kpis.alunos_saida}
              prefix={<UserDeleteOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Cancelamentos no mês
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Taxa de Retenção"
              value={kpis.taxa_retencao}
              precision={1}
              suffix="%"
              prefix={<TrophyOutlined />}
              valueStyle={{ color: kpis.taxa_retencao >= 90 ? '#52c41a' : '#faad14' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Meta: 95%
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Receita Média/Aluno"
              value={kpis.receita_media_aluno}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#1890ff' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Ticket médio
            </p>
          </Card>
        </Col>
      </Row>

      {/* Gráficos */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card title="Evolução de Matrículas (6 meses)">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={evolucao_matriculas}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="matriculas" 
                  stroke="#52c41a" 
                  strokeWidth={3}
                  name="Matrículas"
                  dot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Novas Matrículas por Modalidade">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={matriculas_por_modalidade}
                  dataKey="matriculas"
                  nameKey="modalidade"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => `${entry.modalidade}: ${entry.matriculas}`}
                >
                  {matriculas_por_modalidade.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Receita por Escola */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Top 10 Escolas por Receita">
            <Table
              dataSource={receita_por_escola}
              columns={colunasReceitaEscola}
              pagination={false}
              size="small"
              rowKey={(record, index) => index}
              scroll={{ y: 400 }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Receita por Escola (Gráfico)">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={receita_por_escola.slice(0, 5)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="escola" type="category" width={120} />
                <Tooltip formatter={(value) => formatarMoeda(value)} />
                <Bar dataKey="receita" fill="#1890ff" name="Receita" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardComercial;
